import { Box, Button, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../../services/logger/logger';

interface ErrorBoundaryInnerProps {
  children?: ReactNode;
  // Keep type simple here to avoid TS complaining about arguments count
  t: (_key: string, _options?: any) => string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryInner extends Component<ErrorBoundaryInnerProps, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('Uncaught error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  public render() {
    const { t } = this.props;

    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          width="100%"
          p={3}
        >
          <Stack spacing={3} alignItems="center">
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: (theme: any) => theme.palette.text.primary,
                textAlign: 'center',
              }}
            >
              {t('common.somethingWentWrong')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: (theme: any) => theme.palette.textMedium,
                maxWidth: '450px',
                textAlign: 'center',
                lineHeight: 1.6,
              }}
            >
              {t('common.errorBoundaryMessage')}
            </Typography>
            {this.state.error && (
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  backgroundColor: (theme: any) => theme.palette.background.default,
                  border: (theme: any) => `1px solid ${theme.palette.borderLight}`,
                  borderRadius: '8px',
                  width: '100%',
                  maxWidth: '600px',
                  maxHeight: '150px',
                  overflowY: 'auto',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    color: (theme: any) => theme.palette.text.secondary,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    fontSize: '0.75rem',
                  }}
                >
                  {this.state.error.stack || this.state.error.toString()}
                </Typography>
              </Box>
            )}
            <Button
              variant="contained"
              disableElevation
              onClick={this.handleReset}
              sx={{
                mt: 1,
                textTransform: 'none',
                px: 4,
                py: 1,
                borderRadius: '6px',
                fontWeight: 600,
                backgroundColor: (theme: any) => theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: (theme: any) => theme.palette.primary.dark,
                },
              }}
            >
              {t('common.refreshPage')}
            </Button>
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}

interface ErrorBoundaryProps {
  children?: ReactNode;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const { t } = useTranslation();
  return <ErrorBoundaryInner t={t}>{children}</ErrorBoundaryInner>;
};

export default ErrorBoundary;
